import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Container,
  Breadcrumbs,
  Link,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import { Security as SecurityIcon, Search as SearchIcon } from '@mui/icons-material';

export const LicenseStatusCheck = () => {
  const [licenseNumber, setLicenseNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any>(null);

  const handleCheckLicense = async () => {
    if (!licenseNumber.trim()) {return;}
    
    setLoading(true);
    // TODO: Implement actual license check API call
    setTimeout(() => {
      setResults({
        holder: 'Sample Driver Name',
        status: 'Valid',
        classStatus: 'Class Match',
        checkDate: new Date().toLocaleDateString(),
      });
      setLoading(false);
    }, 2000);
  };

  const handleClearFilter = () => {
    setLicenseNumber('');
    setResults(null);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link 
          color="inherit" 
          href="/services" 
          sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          Services
        </Link>
        <Typography color="text.primary">Driver's License Status Check</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
          License Status Check
        </Typography>
      </Box>

      {/* Service Description */}
      <Card sx={{ mb: 4, boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)' }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <SecurityIcon sx={{ fontSize: 48, color: '#1976d2', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Instantly check the validity of any Canadian driver's licence. Discover if a licence is 
            Valid, Suspended, Driver Interlock Status, Not found/fraudulent, and more.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, maxWidth: '800px', mx: 'auto' }}>
            The Drivers Licence Status Check instantly provides you the current status of a licence. This service is 
            useful when you don't have time to order a Driver's Abstract/Motor Vehicle Record (MVR) but you need 
            confirmation that a licence is valid. If you're a commercial vehicle operator, you are responsible for ensuring 
            the driver of your vehicle is appropriately licenced, and failure to do so can have a negative impact on your 
            carrier rating or CVOR score.
          </Typography>
        </CardContent>
      </Card>

      {/* Search Interface */}
      <Card sx={{ mb: 4, boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)' }}>
        <CardContent sx={{ p: 4 }}>
          <Button
            variant="contained"
            startIcon={<SecurityIcon />}
            sx={{
              mb: 3,
              backgroundColor: '#4ecdc4',
              '&:hover': { backgroundColor: '#45b7d1' },
            }}
          >
            Check a license
          </Button>

          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Name or License #"
              value={licenseNumber}
              onChange={(e) => setLicenseNumber(e.target.value)}
              sx={{ minWidth: 300, flexGrow: 1 }}
              onKeyPress={(e) => e.key === 'Enter' && handleCheckLicense()}
            />
            <Button
              variant="contained"
              onClick={handleCheckLicense}
              disabled={loading || !licenseNumber.trim()}
              startIcon={<SearchIcon />}
            >
              {loading ? 'Checking...' : 'Filter'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearFilter}
            >
              Remove filter
            </Button>
          </Box>

          {/* Results Table */}
          {results ? (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">License Status Results</Typography>
              </Box>
              
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                {/* Table Header */}
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr 1fr 1fr',
                  bgcolor: '#f5f5f5',
                  p: 2,
                  borderBottom: '1px solid #e0e0e0'
                }}>
                  <Typography variant="subtitle2" fontWeight="bold">License holder</Typography>
                  <Typography variant="subtitle2" fontWeight="bold">License Status</Typography>
                  <Typography variant="subtitle2" fontWeight="bold">License Class Status</Typography>
                  <Typography variant="subtitle2" fontWeight="bold">Check date</Typography>
                </Box>
                
                {/* Table Row */}
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr 1fr 1fr',
                  p: 2,
                  alignItems: 'center'
                }}>
                  <Typography>{results.holder}</Typography>
                  <Chip 
                    label={results.status} 
                    color="success" 
                    size="small"
                    sx={{ width: 'fit-content' }}
                  />
                  <Typography>{results.classStatus}</Typography>
                  <Typography>{results.checkDate}</Typography>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                No license status checks to display
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Pricing */}
      <Card sx={{ mb: 4, boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)' }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Protect your company, ensure client licenses are real and valid, and more.
          </Typography>
          <Typography variant="h3" sx={{ color: '#1976d2', fontWeight: 'bold', my: 2 }}>
            $5.00
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Per Driver's License Status Check
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              mt: 2,
              backgroundColor: '#4ecdc4',
              '&:hover': { backgroundColor: '#45b7d1' },
            }}
          >
            Check a license
          </Button>
        </CardContent>
      </Card>

      {/* Response Information */}
      <Card sx={{ boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom>
            License Status Responses:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="'Valid' response means that the driver's licence number entered is not suspended, cancelled or expired."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="'Not Valid' response means that the driver's licence number entered is suspended, cancelled, expired or a combination thereof."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="'Interlock' response means that the driver's licence number entered has an ignition interlock condition. The driver must only drive vehicles equipped with an approved ignition interlock device."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="'Not Found' response means that the driver's licence number entered is not a recognized driver's licence number and does not exist in the jurisdictions database."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="'Invalid DOB' response means that the driver's date of birth entered is not a correct match to the licence number provided, but the licence number exists with the jurisdiction."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="'Not Completed' response means that the jurisdiction system is down or the system is temporarily unable to complete the transaction."
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Class Responses:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="'Class Match' response means the class of licence provided matches the class in the jurisdictions database."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="'Class Match/GDL' response means the class of licence provided is correct but has a Graduated Driver Licencing (GDL) restrictions set by the jurisdiction."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="'Downgraded' response means the class of licence provided has been downgraded from the original class provided and not able to drive vehicles originally qualified for."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="'Not Provided' response means no class of licence was provided to check."
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>
    </Container>
  );
}; 